'use client';

// Комментарий на русском: утилиты для вычисления/кэша waveform голосовых.
// Зачем: расчёты независимы от UI, переиспользуемы и не дублируются по компонентам.

export const WAVE_BARS_COUNT = 48;

export type VoiceWave = {
  bars: number[];
  dur?: number;
};

const WAVE_LS_INDEX = 'lisad_voice_wave_index_v1';
const WAVE_LS_LIMIT = 200;

// Комментарий на русском: память на время сессии (быстрее localStorage).
const mem = new Map<string, VoiceWave>();
const inflight = new Map<string, Promise<VoiceWave | null>>();

function touchMem(src: string, v: VoiceWave) {
  try {
    if (mem.has(src)) mem.delete(src);
    mem.set(src, v);
    while (mem.size > WAVE_LS_LIMIT + 40) {
      const k = mem.keys().next().value as string | undefined;
      if (!k) break;
      mem.delete(k);
    }
  } catch {
    // ignore
  }
}

function isSkippableSrc(src: string): boolean {
  return !src || src.startsWith('data:') || src.startsWith('blob:');
}

function storageKeyForSrc(src: string): string {
  // Комментарий на русском: FNV-1a (короткий и быстрый хэш).
  let h = 2166136261;
  for (let i = 0; i < src.length; i++) {
    h ^= src.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `lisad_voice_wave_v1:${(h >>> 0).toString(16)}`;
}

function touchIndex(itemKey: string) {
  try {
    const now = Date.now();
    const raw = localStorage.getItem(WAVE_LS_INDEX);
    const arr = (raw ? (JSON.parse(raw) as any[]) : []) || [];
    const next = Array.isArray(arr) ? arr.filter((x) => x && x.k !== itemKey) : [];
    next.push({ k: itemKey, t: now });
    while (next.length > WAVE_LS_LIMIT) {
      const rm = next.shift();
      const k = rm?.k;
      if (typeof k === 'string' && k) {
        try {
          localStorage.removeItem(k);
        } catch {}
      }
    }
    localStorage.setItem(WAVE_LS_INDEX, JSON.stringify(next.slice(-WAVE_LS_LIMIT)));
  } catch {
    // ignore
  }
}

function normalizeBars(bars: any): number[] | null {
  if (!Array.isArray(bars) || bars.length !== WAVE_BARS_COUNT) return null;
  return bars.map((v: any) => Math.max(0.1, Math.min(1, Number(v) || 0.1)));
}

function readFromStorage(src: string): VoiceWave | null {
  try {
    if (typeof window === 'undefined') return null;
    if (isSkippableSrc(src)) return null;
    const key = storageKeyForSrc(src);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    const bars = normalizeBars(Array.isArray(data?.b) ? data.b : (Array.isArray(data) ? data : null));
    if (!bars) return null;
    const d = Number(data?.d);
    touchIndex(key);
    return { bars, dur: Number.isFinite(d) && d > 0 ? d : undefined };
  } catch {
    return null;
  }
}

function writeToStorage(src: string, v: VoiceWave): void {
  try {
    if (typeof window === 'undefined') return;
    if (isSkippableSrc(src)) return;
    const bars = normalizeBars(v?.bars);
    if (!bars) return;
    const key = storageKeyForSrc(src);
    const payload = {
      // Комментарий на русском: округляем, чтобы уменьшить размер.
      b: bars.map((x) => Math.round(x * 1000) / 1000),
      d: Number.isFinite(v?.dur as any) && (v.dur as number) > 0 ? Math.round((v.dur as number) * 1000) / 1000 : undefined,
    };
    localStorage.setItem(key, JSON.stringify(payload));
    touchIndex(key);
  } catch {
    // ignore
  }
}

async function decodeWave(src: string): Promise<VoiceWave | null> {
  try {
    if (typeof window === 'undefined') return null;
    if (isSkippableSrc(src)) return null;

    const res = await fetch(src);
    if (!res.ok) return null;
    const buf = await res.arrayBuffer();
    if (buf.byteLength > 10 * 1024 * 1024) return null;

    const AudioCtx = (window.AudioContext || (window as any).webkitAudioContext) as any;
    if (!AudioCtx) return null;
    const ctx = new AudioCtx();

    const audioBuffer: AudioBuffer = await new Promise((resolve, reject) => {
      try {
        const p = ctx.decodeAudioData(buf, resolve, reject);
        if (p && typeof p.then === 'function') (p as Promise<AudioBuffer>).then(resolve).catch(reject);
      } catch (e) {
        reject(e);
      }
    });

    const data = audioBuffer.getChannelData(0);
    const n = WAVE_BARS_COUNT;
    const block = Math.max(1, Math.floor(data.length / n));
    const step = Math.max(1, Math.floor(block / 48));

    const peaks = new Array(n).fill(0) as number[];
    for (let i = 0; i < n; i++) {
      let max = 0;
      const start = i * block;
      const end = Math.min(data.length, start + block);
      for (let j = start; j < end; j += step) {
        const v = Math.abs(data[j] || 0);
        if (v > max) max = v;
      }
      peaks[i] = max;
    }
    const m = Math.max(0.0001, ...peaks);
    const bars = peaks.map((v) => 0.18 + 0.82 * Math.min(1, v / m));

    try {
      await ctx.close?.();
    } catch {}

    return { bars, dur: Number(audioBuffer.duration) || 0 };
  } catch {
    return null;
  }
}

export function getCachedVoiceWave(src: string): VoiceWave | null {
  if (!src) return null;
  const v = mem.get(src);
  if (v?.bars?.length === WAVE_BARS_COUNT) {
    touchMem(src, v);
    return v;
  }
  const ls = readFromStorage(src);
  if (ls?.bars?.length === WAVE_BARS_COUNT) {
    touchMem(src, ls);
    return ls;
  }
  return null;
}

export function prefetchVoiceWave(
  src: string,
  opts?: { rank?: number; timeoutMs?: number }
): Promise<VoiceWave | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (isSkippableSrc(src)) return Promise.resolve(null);

  const cached = getCachedVoiceWave(src);
  if (cached) return Promise.resolve(cached);

  const p0 = inflight.get(src);
  if (p0) return p0;

  // Комментарий на русском: rank может быть большим (мы префетчим до 100 голосовых), поэтому НЕ зажимаем в 0..6.
  // Зачем: иначе всё стартует почти одновременно и грузит сеть/CPU.
  const rank = Math.max(0, Math.min(200, (opts?.rank ?? 0) | 0));
  const rankDelay = rank * 300;
  const idleTimeout = Math.max(200, (opts?.timeoutMs ?? 900) | 0);

  const p = new Promise<VoiceWave | null>((resolve) => {
    const run = async () => {
      const again = getCachedVoiceWave(src);
      if (again) return resolve(again);
      const decoded = await decodeWave(src);
      if (!decoded?.bars?.length) return resolve(null);
      touchMem(src, decoded);
      writeToStorage(src, decoded);
      resolve(decoded);
    };

    const schedule = () => {
      if (typeof (window as any).requestIdleCallback === 'function') {
        (window as any).requestIdleCallback(() => void run(), { timeout: idleTimeout });
      } else {
        window.setTimeout(() => void run(), 120);
      }
    };

    if (rankDelay) window.setTimeout(schedule, rankDelay);
    else schedule();
  }).finally(() => {
    inflight.delete(src);
  });

  inflight.set(src, p);
  return p;
}


